import type { ReactNode, SVGProps } from 'react'

type IconProps = SVGProps<SVGSVGElement> & {
  icon: string
}

interface BaseSvgProps extends SVGProps<SVGSVGElement> {
  children: ReactNode
  filled?: boolean
}

function BaseSvg({ children, filled = false, strokeWidth = 1.5, ...props }: BaseSvgProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      aria-hidden="true"
      fill={filled ? 'currentColor' : 'none'}
      stroke={filled ? 'none' : 'currentColor'}
      strokeWidth={filled ? undefined : strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      {children}
    </svg>
  )
}

function BrandTextIcon({ text, labelSize = 7, ...props }: SVGProps<SVGSVGElement> & { text: string; labelSize?: number }) {
  return (
    <BaseSvg {...props}>
      <circle cx="12" cy="12" r="9" />
      <text
        x="12"
        y="13.5"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={labelSize}
        fontWeight="700"
        fill="currentColor"
        stroke="none"
        fontFamily="system-ui, sans-serif"
      >
        {text}
      </text>
    </BaseSvg>
  )
}

function renderIcon(icon: string, props: SVGProps<SVGSVGElement>) {
  switch (icon) {
    case 'heroicons:home':
      return (
        <BaseSvg {...props}>
          <path d="M3 10.5 12 3l9 7.5" />
          <path d="M5.25 9.75V21h13.5V9.75" />
          <path d="M9.75 21v-6h4.5v6" />
        </BaseSvg>
      )
    case 'heroicons:document-text':
      return (
        <BaseSvg {...props}>
          <path d="M7.5 3.75h7.5l3 3v13.5H7.5z" />
          <path d="M15 3.75v3h3" />
          <path d="M9.75 11.25h5.25M9.75 14.25h5.25M9.75 17.25h3.75" />
        </BaseSvg>
      )
    case 'heroicons:user':
      return (
        <BaseSvg {...props}>
          <circle cx="12" cy="8" r="3.25" />
          <path d="M5 19.25c1.8-2.9 4.1-4.25 7-4.25s5.2 1.35 7 4.25" />
        </BaseSvg>
      )
    case 'heroicons:chat-bubble-left-right':
      return (
        <BaseSvg {...props}>
          <path d="M5.75 5.75h12.5a2.5 2.5 0 0 1 2.5 2.5v6.25a2.5 2.5 0 0 1-2.5 2.5h-6.5l-3.5 3V17h-2.5a2.5 2.5 0 0 1-2.5-2.5V8.25a2.5 2.5 0 0 1 2.5-2.5Z" />
          <path d="M8.25 10h7.5M8.25 12.75h5.5" />
        </BaseSvg>
      )
    case 'heroicons:moon-solid':
      return (
        <BaseSvg {...props} filled>
          <path d="M14.8 2.5a8.75 8.75 0 1 0 6.7 14.1A9 9 0 0 1 14.8 2.5Z" />
        </BaseSvg>
      )
    case 'heroicons:sun-solid':
      return (
        <BaseSvg {...props}>
          <circle cx="12" cy="12" r="4.25" fill="currentColor" stroke="none" />
          <path d="M12 1.75v2.5M12 19.75v2.5M4.75 4.75l1.8 1.8M17.45 17.45l1.8 1.8M1.75 12h2.5M19.75 12h2.5M4.75 19.25l1.8-1.8M17.45 6.55l1.8-1.8" />
        </BaseSvg>
      )
    case 'heroicons:newspaper':
      return (
        <BaseSvg {...props}>
          <path d="M5.25 5.25h11.25a2.25 2.25 0 0 1 2.25 2.25v9a2.25 2.25 0 0 1-2.25 2.25H7.5A2.25 2.25 0 0 1 5.25 16.5z" />
          <path d="M5.25 8.25H3.75v8.25A2.25 2.25 0 0 0 6 18.75" />
          <path d="M8.25 9.75h6.75M8.25 12.75h6.75M8.25 15.75h4.5" />
        </BaseSvg>
      )
    case 'heroicons:star-solid':
      return (
        <BaseSvg {...props} filled>
          <path d="m12 3.75 2.54 5.15 5.69.83-4.12 4.02.97 5.68L12 16.77l-5.08 2.66.97-5.68L3.77 9.73l5.69-.83z" />
        </BaseSvg>
      )
    case 'heroicons:exclamation-triangle':
      return (
        <BaseSvg {...props}>
          <path d="M12 4.5 3.75 18.75h16.5L12 4.5Z" />
          <path d="M12 9v4.5M12 16.5h.01" />
        </BaseSvg>
      )
    case 'heroicons:arrow-top-right-on-square':
      return (
        <BaseSvg {...props}>
          <path d="M8.25 8.25h-1.5A2.25 2.25 0 0 0 4.5 10.5v6.75a2.25 2.25 0 0 0 2.25 2.25h6.75a2.25 2.25 0 0 0 2.25-2.25v-1.5" />
          <path d="M10.5 13.5 19.5 4.5" />
          <path d="M13.5 4.5h6v6" />
        </BaseSvg>
      )
    case 'heroicons:arrow-path':
      return (
        <BaseSvg {...props}>
          <path d="M4.5 10.5A7.5 7.5 0 0 1 17.3 5.2L19.5 7.5" />
          <path d="M19.5 3.75V7.5h-3.75" />
          <path d="M19.5 13.5A7.5 7.5 0 0 1 6.7 18.8L4.5 16.5" />
          <path d="M8.25 16.5H4.5v3.75" />
        </BaseSvg>
      )
    case 'heroicons:arrow-down-tray':
      return (
        <BaseSvg {...props}>
          <path d="M12 3.75v10.5" />
          <path d="m8.25 10.5 3.75 3.75 3.75-3.75" />
          <path d="M4.5 15.75v1.5A2.25 2.25 0 0 0 6.75 19.5h10.5a2.25 2.25 0 0 0 2.25-2.25v-1.5" />
        </BaseSvg>
      )
    case 'heroicons:check-circle-solid':
      return (
        <BaseSvg {...props} filled>
          <path d="M12 2.75a9.25 9.25 0 1 0 0 18.5 9.25 9.25 0 0 0 0-18.5Zm4.1 6.89-5 5a1 1 0 0 1-1.42 0l-1.78-1.78a1 1 0 1 1 1.42-1.42l1.07 1.07 4.29-4.29a1 1 0 1 1 1.42 1.42Z" />
        </BaseSvg>
      )
    case 'heroicons:chevron-right':
      return (
        <BaseSvg {...props}>
          <path d="m9 5.25 6 6-6 6" />
        </BaseSvg>
      )
    case 'heroicons:chevron-down':
      return (
        <BaseSvg {...props}>
          <path d="m6 9 6 6 6-6" />
        </BaseSvg>
      )
    case 'heroicons:arrow-up':
      return (
        <BaseSvg {...props}>
          <path d="M12 19.5V4.5" />
          <path d="m7.5 9 4.5-4.5L16.5 9" />
        </BaseSvg>
      )
    case 'heroicons:arrow-down':
      return (
        <BaseSvg {...props}>
          <path d="M12 4.5v15" />
          <path d="m16.5 15-4.5 4.5L7.5 15" />
        </BaseSvg>
      )
    case 'heroicons:arrow-left':
      return (
        <BaseSvg {...props}>
          <path d="M19.5 12H4.5" />
          <path d="m9 7.5-4.5 4.5L9 16.5" />
        </BaseSvg>
      )
    case 'heroicons:paper-airplane':
      return (
        <BaseSvg {...props}>
          <path d="M3.75 11.25 19.5 4.5l-4.5 15-3.75-4.5-4.5-3.75Z" />
          <path d="M11.25 15 19.5 4.5" />
        </BaseSvg>
      )
    case 'heroicons:map-pin-solid':
      return (
        <BaseSvg {...props} filled>
          <path d="M12 2.75a6 6 0 0 0-6 6c0 4.43 6 12.5 6 12.5s6-8.07 6-12.5a6 6 0 0 0-6-6Zm0 8.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5Z" />
        </BaseSvg>
      )
    case 'heroicons:map-pin':
      return (
        <BaseSvg {...props}>
          <path d="M12 21s6-8.07 6-12.5a6 6 0 1 0-12 0C6 12.93 12 21 12 21Z" />
          <circle cx="12" cy="8.5" r="2.25" />
        </BaseSvg>
      )
    case 'heroicons:x-mark':
      return (
        <BaseSvg {...props}>
          <path d="M6 6 18 18M18 6 6 18" />
        </BaseSvg>
      )
    case 'heroicons:ellipsis-horizontal':
      return (
        <BaseSvg {...props}>
          <circle cx="6.5" cy="12" r="1.25" fill="currentColor" stroke="none" />
          <circle cx="12" cy="12" r="1.25" fill="currentColor" stroke="none" />
          <circle cx="17.5" cy="12" r="1.25" fill="currentColor" stroke="none" />
        </BaseSvg>
      )
    case 'heroicons:arrow-up-tray':
      return (
        <BaseSvg {...props}>
          <path d="M12 15.75V4.5" />
          <path d="m8.25 8.25 3.75-3.75 3.75 3.75" />
          <path d="M4.5 15.75v1.5A2.25 2.25 0 0 0 6.75 19.5h10.5a2.25 2.25 0 0 0 2.25-2.25v-1.5" />
        </BaseSvg>
      )
    case 'heroicons:plus-circle':
      return (
        <BaseSvg {...props}>
          <circle cx="12" cy="12" r="8.25" />
          <path d="M12 8.25v7.5M8.25 12h7.5" />
        </BaseSvg>
      )
    case 'heroicons:exclamation-circle':
      return (
        <BaseSvg {...props}>
          <circle cx="12" cy="12" r="8.25" />
          <path d="M12 8.25v4.5M12 15.75h.01" />
        </BaseSvg>
      )
    case 'heroicons:bell-slash':
      return (
        <BaseSvg {...props}>
          <path d="M9.5 18a2.5 2.5 0 0 0 5 0" />
          <path d="M5.5 14.75h13L16.75 13V9.75a4.75 4.75 0 0 0-8.29-3.14" />
          <path d="M3.75 3.75 20.25 20.25" />
        </BaseSvg>
      )
    case 'heroicons:bell-alert-solid':
      return (
        <BaseSvg {...props} filled>
          <path d="M12 2.75a5.25 5.25 0 0 0-5.25 5.25V11l-1.6 2.67A1.5 1.5 0 0 0 6.44 16h11.12a1.5 1.5 0 0 0 1.29-2.33L17.25 11V8A5.25 5.25 0 0 0 12 2.75Zm0 18.5a2.75 2.75 0 0 0 2.67-2h-5.34a2.75 2.75 0 0 0 2.67 2Zm4.75-14.5h2.5v2.5h-2.5z" />
        </BaseSvg>
      )
    case 'heroicons:bell-alert':
      return (
        <BaseSvg {...props}>
          <path d="M12 21a2.75 2.75 0 0 0 2.67-2H9.33A2.75 2.75 0 0 0 12 21Z" />
          <path d="M5.5 15.25h13L16.75 12.5V8a4.75 4.75 0 1 0-9.5 0v4.5z" />
          <path d="M17.25 4.5h.01M17.25 6.75v.01" />
        </BaseSvg>
      )
    case 'heroicons:check':
      return (
        <BaseSvg {...props}>
          <path d="m5.25 12.75 4.5 4.5 9-9" />
        </BaseSvg>
      )
    case 'heroicons:eye':
      return (
        <BaseSvg {...props}>
          <path d="M2.75 12s3.5-6 9.25-6 9.25 6 9.25 6-3.5 6-9.25 6S2.75 12 2.75 12Z" />
          <circle cx="12" cy="12" r="2.75" />
        </BaseSvg>
      )
    case 'heroicons:eye-slash':
      return (
        <BaseSvg {...props}>
          <path d="M3 3 21 21" />
          <path d="M10.6 6.1A9.8 9.8 0 0 1 12 6c5.75 0 9.25 6 9.25 6a16.2 16.2 0 0 1-3.28 3.77" />
          <path d="M6.52 8.02A16.2 16.2 0 0 0 2.75 12s3.5 6 9.25 6c1.4 0 2.7-.35 3.9-.98" />
          <path d="M10.5 10.5a2.5 2.5 0 0 0 3 3" />
        </BaseSvg>
      )
    case 'heroicons:clock':
      return (
        <BaseSvg {...props}>
          <circle cx="12" cy="12" r="8.25" />
          <path d="M12 7.75v4.5l3 1.75" />
        </BaseSvg>
      )
    case 'heroicons:check-circle':
      return (
        <BaseSvg {...props}>
          <circle cx="12" cy="12" r="8.25" />
          <path d="m8.5 12.25 2.25 2.25 4.75-4.75" />
        </BaseSvg>
      )
    case 'heroicons:x-circle':
      return (
        <BaseSvg {...props}>
          <circle cx="12" cy="12" r="8.25" />
          <path d="M9.25 9.25 14.75 14.75M14.75 9.25l-5.5 5.5" />
        </BaseSvg>
      )
    case 'heroicons:phone':
      return (
        <BaseSvg {...props}>
          <path d="M6 4.5h3l1.5 3-1.75 1.75a14.5 14.5 0 0 0 6 6L16.5 13l3 1.5v3a1.5 1.5 0 0 1-1.5 1.5C11.06 19 5 12.94 5 5.99A1.5 1.5 0 0 1 6.5 4.5Z" />
        </BaseSvg>
      )
    case 'heroicons:cake':
      return (
        <BaseSvg {...props}>
          <path d="M7.5 10.5h9v7.5h-9z" />
          <path d="M6 18h12" />
          <path d="M8.25 10.5V8.25M12 10.5V6.75M15.75 10.5V8.25" />
          <path d="M12 6.75c-.75-.5-.75-1.5 0-2.25.75.75.75 1.75 0 2.25Z" />
        </BaseSvg>
      )
    case 'heroicons:calendar-days':
      return (
        <BaseSvg {...props}>
          <path d="M6.75 4.5v3M17.25 4.5v3" />
          <rect x="4.5" y="6" width="15" height="13.5" rx="2.25" />
          <path d="M4.5 9.75h15M9 13.5h2.25M12.75 13.5H15M9 16.5h2.25" />
        </BaseSvg>
      )
    case 'heroicons:arrow-right-on-rectangle':
      return (
        <BaseSvg {...props}>
          <path d="M14.25 7.5V5.25A2.25 2.25 0 0 0 12 3H6.75A2.25 2.25 0 0 0 4.5 5.25v13.5A2.25 2.25 0 0 0 6.75 21H12a2.25 2.25 0 0 0 2.25-2.25V16.5" />
          <path d="M10.5 12h9" />
          <path d="m16.5 8.25 3.75 3.75-3.75 3.75" />
        </BaseSvg>
      )
    case 'heroicons:wrench-screwdriver':
      return (
        <BaseSvg {...props}>
          <path d="m14.25 6.75 3-3 3 3-3 3" />
          <path d="m13.5 7.5-8.25 8.25a2.12 2.12 0 0 0 3 3L16.5 10.5" />
          <path d="m6.75 6.75 4.5 4.5" />
        </BaseSvg>
      )
    case 'heroicons:credit-card':
      return (
        <BaseSvg {...props}>
          <rect x="3.75" y="6" width="16.5" height="12" rx="2.25" />
          <path d="M3.75 9.75h16.5M7.5 14.25h3.75" />
        </BaseSvg>
      )
    case 'heroicons:wifi':
      return (
        <BaseSvg {...props}>
          <path d="M4.5 9.75a11.5 11.5 0 0 1 15 0" />
          <path d="M7.5 12.75a7.25 7.25 0 0 1 9 0" />
          <path d="M10.25 15.5a3.5 3.5 0 0 1 3.5 0" />
          <circle cx="12" cy="18.5" r=".75" fill="currentColor" stroke="none" />
        </BaseSvg>
      )
    case 'heroicons:tv':
      return (
        <BaseSvg {...props}>
          <rect x="3.75" y="6" width="16.5" height="10.5" rx="2.25" />
          <path d="M9 19.5h6M10.5 16.5v3M13.5 16.5v3" />
        </BaseSvg>
      )
    case 'heroicons:question-mark-circle':
      return (
        <BaseSvg {...props}>
          <circle cx="12" cy="12" r="8.25" />
          <path d="M9.75 9.75a2.25 2.25 0 1 1 4.14 1.22c-.56.78-1.89 1.32-1.89 2.53" />
          <path d="M12 16.5h.01" />
        </BaseSvg>
      )
    case 'simple-icons:telegram':
      return (
        <BaseSvg {...props} filled>
          <path d="M11.944 0A12 12 0 1 0 12 24a12 12 0 0 0-.056-24zm4.962 7.224c.1-.002.321.023.465.14a.5.5 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024q-.159.037-5.061 3.345-.72.495-1.302.48c-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789q.04-.324.893-.663 5.247-2.286 6.998-3.014c3.332-1.386 4.025-1.627 4.476-1.635" />
        </BaseSvg>
      )
    case 'simple-icons:vk':
      return <BrandTextIcon text="VK" labelSize={6.5} {...props} />
    case 'arcticons:max-messenger':
      return <BrandTextIcon text="MAX" labelSize={5.2} {...props} />
    case 'svg-spinners:90-ring-with-bg':
      return (
        <BaseSvg {...props}>
          <circle cx="12" cy="12" r="8" opacity="0.2" />
          <path d="M12 4a8 8 0 0 1 8 8" />
        </BaseSvg>
      )
    default:
      return (
        <BaseSvg {...props}>
          <rect x="4.5" y="4.5" width="15" height="15" rx="3" />
          <path d="M9.75 9.75a2.25 2.25 0 0 1 4.14 1.22c-.56.78-1.89 1.32-1.89 2.53" />
          <path d="M12 16.5h.01" />
        </BaseSvg>
      )
  }
}

export function Icon({ icon, ...props }: IconProps) {
  return renderIcon(icon, props)
}

export const InlineIcon = Icon

export function addIcon() {
  // Offline icon registry is static in this project.
}

export function addCollection() {
  // Offline icon registry is static in this project.
}
